/**
 * ErpSettingsHubPage
 *
 * Hub central de configurações do ERP (layout alinhado ao dashboard financeiro).
 */

import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Settings,
  RotateCcw,
  Target,
  Trophy,
  Clock,
  SlidersHorizontal,
  Loader2,
} from 'lucide-react'
import { usePermissions } from '../../../hooks/usePermissions'
import { ERP_SETTINGS_MODULES } from '../../../config/erpSettingsModules'
import { erpSettingsService } from '../../../services/erpSettingsService'
import './ErpSettingsHubPage.css'

function formatCurrency(value) {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(Number(value) || 0)
}

function ErpSettingsHubPage() {
  const navigate = useNavigate()
  const { hasPermission } = usePermissions()
  const [loading, setLoading] = useState(true)
  const [refreshing, setRefreshing] = useState(false)
  const [error, setError] = useState(null)
  const [cockpitSummary, setCockpitSummary] = useState(null)

  const modules = useMemo(
    () => ERP_SETTINGS_MODULES.filter((item) => hasPermission(item.module, item.action)),
    [hasPermission]
  )

  const loadSummary = useCallback(async ({ isRefresh = false } = {}) => {
    if (isRefresh) {
      setRefreshing(true)
    } else {
      setLoading(true)
    }
    setError(null)

    try {
      const canViewCockpit = modules.some((item) => item.id === 'cockpit')
      if (canViewCockpit) {
        const response = await erpSettingsService.getNamespace('cockpit')
        if (response?.success) {
          setCockpitSummary(response.data?.settings || null)
        }
      } else {
        setCockpitSummary(null)
      }
    } catch (err) {
      console.error('Erro ao carregar resumo das configurações:', err)
      setError('Não foi possível carregar o resumo das configurações.')
      setCockpitSummary(null)
    } finally {
      setLoading(false)
      setRefreshing(false)
    }
  }, [modules])

  useEffect(() => {
    loadSummary()
  }, [loadSummary])

  const summaryCards = useMemo(() => {
    if (!cockpitSummary) return []

    return [
      {
        label: 'Meta mensal (Cockpit)',
        value: formatCurrency(cockpitSummary.monthly_goal),
        icon: Target,
        color: 'text-emerald-500',
        bg: 'bg-emerald-50',
      },
      {
        label: 'Marcos de prêmio',
        value: String(cockpitSummary.milestones?.length || 0),
        icon: Trophy,
        color: 'text-amber-500',
        bg: 'bg-amber-50',
      },
      {
        label: 'SLA de resposta',
        value: `${cockpitSummary.sla_response_minutes || 0} min`,
        icon: Clock,
        color: 'text-blue-500',
        bg: 'bg-blue-50',
      },
    ]
  }, [cockpitSummary])

  if (loading && !cockpitSummary) {
    return (
      <div className="erp-settings-loading">
        <Loader2 size={28} className="erp-settings-reload-icon is-spinning" />
        <p>Carregando configurações do ERP...</p>
      </div>
    )
  }

  return (
    <div className={`erp-settings-dashboard${refreshing ? ' is-refreshing' : ''}`}>
      <div className="erp-settings-header">
        <div>
          <h1 className="erp-settings-title">
            Configurações <span className="erp-settings-title-highlight">ERP</span>
            {refreshing && (
              <RotateCcw size={20} className="erp-settings-refresh-icon" aria-hidden />
            )}
          </h1>
          <p className="erp-settings-subtitle">
            Parâmetros do sistema ajustáveis por módulo
          </p>
        </div>
        <div className="erp-settings-header-actions">
          <button
            type="button"
            onClick={() => loadSummary({ isRefresh: true })}
            className="erp-settings-reload-btn"
            disabled={loading || refreshing}
            aria-label="Atualizar configurações"
            title="Atualizar"
          >
            <RotateCcw
              size={18}
              className={refreshing ? 'erp-settings-reload-icon is-spinning' : 'erp-settings-reload-icon'}
            />
          </button>
          <div className="erp-settings-badge">
            <SlidersHorizontal size={14} className="erp-settings-badge-icon" />
            <span>{modules.length} módulo{modules.length === 1 ? '' : 's'}</span>
          </div>
        </div>
      </div>

      {error && (
        <div className="erp-settings-error-banner" role="alert">
          <p>{error}</p>
          <button type="button" onClick={() => loadSummary({ isRefresh: true })}>
            Tentar novamente
          </button>
        </div>
      )}

      <div className="erp-settings-shortcuts-section">
        <h3 className="erp-settings-section-title">Módulos</h3>
        {modules.length > 0 ? (
          <div className="erp-settings-shortcuts-grid">
            {modules.map((moduleItem) => (
              <button
                key={moduleItem.id}
                type="button"
                onClick={() => navigate(moduleItem.path)}
                className="erp-settings-shortcut-btn"
              >
                <div className={`erp-settings-shortcut-icon ${moduleItem.color}`}>
                  <moduleItem.icon size={22} />
                </div>
                <span className="erp-settings-shortcut-label">{moduleItem.label}</span>
                <span className="erp-settings-shortcut-description">{moduleItem.description}</span>
              </button>
            ))}
          </div>
        ) : (
          <div className="erp-settings-empty">
            <Settings size={40} />
            <p>Nenhum módulo de configuração disponível para o seu perfil.</p>
          </div>
        )}
      </div>

      {summaryCards.length > 0 && (
        <div className="erp-settings-summary-section">
          <h3 className="erp-settings-section-title">Resumo atual — Cockpit</h3>
          <div className="erp-settings-summary-grid">
            {summaryCards.map((card) => (
              <div key={card.label} className="erp-settings-summary-card">
                <div className={`erp-settings-summary-icon ${card.bg} ${card.color}`}>
                  <card.icon size={20} />
                </div>
                <div>
                  <p className="erp-settings-summary-label">{card.label}</p>
                  <p className="erp-settings-summary-value">{card.value}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

export default ErpSettingsHubPage
